'use client';
// Central in-memory store for dynamic data (suppliers, categories, currency)
// Backend integration point: replace with API calls / Zustand / Context

import { SUPPLIERS as INITIAL_SUPPLIERS } from './mockData';

export const MATERIAL_CATEGORIES_DEFAULT = [
  'Fats & Oils', 'Chemicals', 'Fragrance', 'Liquids', 'Waxes', 'Powders', 'Additives', 'Other',
];

export const PRODUCT_CATEGORIES_DEFAULT = [
  'Soap', 'Lip Care', 'Skincare', 'Body Care', 'Bath', 'Hair Care', 'Other',
];

export const PACKAGING_CATEGORIES_DEFAULT = [
  'Bottles', 'Jars', 'Tubes', 'Pouches', 'Boxes', 'Labels', 'Caps & Closures', 'Other',
];

export interface SupplierRecord {
  id: string;
  name: string;
  contactName?: string;
  email?: string;
  phone?: string;
  notes?: string;
}

export const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
];

export type CurrencyCode = typeof CURRENCIES[number]['code'];

// Format a number as currency using the given currency code
export function formatWithCurrency(value: number, currencyCode: CurrencyCode = 'USD'): string {
  const currency = CURRENCIES.find((c) => c.code === currencyCode);
  if (!currency) return `$${value.toFixed(2)}`;
  // Use Intl for proper formatting where possible
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency.symbol}${value.toFixed(2)}`;
  }
}

// Format a unit price with up to 6 decimal places (trims trailing zeros, min 2)
export function formatUnitPrice(value: number, currencyCode: CurrencyCode = 'USD'): string {
  const currency = CURRENCIES.find((c) => c.code === currencyCode);
  const symbol = currency?.symbol ?? '$';
  // Determine how many decimal places are needed (2–6)
  const formatted6 = value.toFixed(6);
  const trimmed = formatted6.replace(/(\.\d*[1-9])0+$/, '$1').replace(/\.00$/, '.00');
  // Count actual significant decimals
  const decimalPart = trimmed.split('.')[1] ?? '00';
  const sigDecimals = Math.max(2, decimalPart.replace(/0+$/, '').length);
  const finalDecimals = Math.min(6, sigDecimals);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: finalDecimals,
      maximumFractionDigits: finalDecimals,
    }).format(value);
  } catch {
    return `${symbol}${value.toFixed(finalDecimals)}`;
  }
}

// Initial supplier records from mock data
export const initialSupplierRecords: SupplierRecord[] = INITIAL_SUPPLIERS.map((name, i) => ({
  id: `sup-${String(i + 1).padStart(3, '0')}`,
  name,
  contactName: '',
  email: '',
  phone: '',
  notes: '',
}));
